import React, { useEffect, useRef } from "react"
import * as am5 from "@amcharts/amcharts5"
import * as am5hierarchy from "@amcharts/amcharts5/hierarchy"
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated"
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting"

import { filterMenuItems, getNumberFormat, transformeData } from "../utils/global"
import {
  createExportMenu,
  configureNumberFormatting,
  configureColorSet,
  clearLegends,
  createLegend,
  resolveMappedColor,
} from "../utils/utils"
import type { ColorMapperRow } from "../utils/utils"
import { formatETLDataToAmchartArray, formatDataAndGroupByToTreeCorrespondanceLevel } from "./utils"

// Types
interface TreeMapChartProps {
  idChart?: string
  width?: string
  height?: string
  colorSetTheme?: string[]
  colorMapper?: ColorMapperRow[]
  dataChart?: any[]
  valueData?: string | string[]
  categoryData?: string | string[]
  numberDecimal?: number
  enableZoom?: boolean
  enableExportMenu?: boolean
  enableNavigationBar?: boolean
  decimalSeparator?: string
  showSpaceForThousand?: boolean
  showAsPercentage?: boolean
  visibilityLegend?: boolean
  displayTypeLegendLabels?: "default" | "truncate" | "wrap"
  legendPosition?: "top" | "bottom" | "left" | "right"
  maxLevelInSameTime?: number
  initialViewText?: string
  columnCornerRadiusTopLeft?: number
  columnCornerRadiusTopRight?: number
  columnCornerRadiusBottomLeft?: number
  columnCornerRadiusBottomRight?: number
  bulletLabelType?: "default" | "value" | "name_and_value"
  bulletLabelColor?: string
  bulletLabelWeight?: "bold" | "normal"
  bulletLabelFontSize?: string
  bulletValueColor?: string
  bulletValueWeight?: "bold" | "normal"
  bulletValueFontSize?: string
  hideLabelIfPercentTooSmall?: boolean
  valuePercentUnderWhichHideLabel?: number
  widgetTitle?: string
  requestLoad?: any
  fieldsData?: any
}

const TreeMapChart: React.FC<TreeMapChartProps> = ({
  idChart,
  width = "100%",
  height = "100%",
  colorSetTheme = ["#01014B", "#FFFFFF", "#000000"],
  colorMapper = [],
  dataChart = [],
  valueData = [],
  categoryData = [],
  numberDecimal = 2,
  enableZoom = true,
  enableExportMenu = false,
  enableNavigationBar = false,
  decimalSeparator = ",",
  showSpaceForThousand = true,
  showAsPercentage = false,
  visibilityLegend = false,
  displayTypeLegendLabels = "default",
  legendPosition = "right",
  maxLevelInSameTime = 1,
  initialViewText = "",
  columnCornerRadiusTopLeft = 0,
  columnCornerRadiusTopRight = 0,
  columnCornerRadiusBottomLeft = 0,
  columnCornerRadiusBottomRight = 0,
  bulletLabelType = "default",
  bulletLabelColor = "#FFFFFF",
  bulletLabelWeight = "normal",
  bulletLabelFontSize = "1rem",
  bulletValueColor = "#FFFFFF",
  bulletValueWeight = "normal",
  bulletValueFontSize = "1rem",
  hideLabelIfPercentTooSmall = false,
  valuePercentUnderWhichHideLabel = 100,
  widgetTitle = "TreeMap",
  requestLoad,
  fieldsData,
}) => {
  const ID = useRef(idChart || `TreeMapChart${Math.random()}`).current
  const chartRef = useRef<am5hierarchy.Treemap | null>(null)
  const rootRef = useRef<am5.Root | null>(null)

  const dataTransformed = transformeData(requestLoad, fieldsData, dataChart)

  const initChart = () => {
    const root = am5.Root.new(ID)
    root._logo?.dispose()
    rootRef.current = root

    root.setThemes([am5themes_Animated.new(root)])

    const chart = root.container.children.push(
      am5hierarchy.Treemap.new(root, {
        valueField: "value",
        categoryField: "name",
        childDataField: "children",
        layoutAlgorithm: "squarify",
        nodePaddingOuter: 0,
        nodePaddingInner: 0,
        initialDepth: maxLevelInSameTime,
        topDepth: 1,
        downDepth: enableZoom ? 1 : 0,
      })
    )

    chartRef.current = chart

    setupChart(chart, root)
    updateChart(chart, root)
  }

  const setupChart = (chart: am5hierarchy.Treemap, root: am5.Root) => {
    // Number formatting
    configureNumberFormatting(root, {
      decimalSeparator,
      numberDecimal,
      showSpaceForThousand,
    })

    // Color set
    configureColorSet(chart, root, colorSetTheme)

    // Note: homeText is not available in amCharts 5 Treemap
    // The initial view is controlled by initialDepth setting

    // Export menu
    createExportMenu(root, {
      enableExportMenu: enableExportMenu ?? false,
      filePrefix: widgetTitle,
      dataSource: dataTransformed,
    })

    // Legend
    const legend = createLegend(root.container, root, {
      visibilityLegend,
      legendPosition,
      displayTypeLegendLabels,
      legendLabelText: "{category}",
    })
  }

  const updateChart = (chart: am5hierarchy.Treemap, root: am5.Root) => {
    const correspondance = formatDataAndGroupByToTreeCorrespondanceLevel(valueData, categoryData)
    const formattedData = formatETLDataToAmchartArray(dataChart, correspondance)

    // Wrap in root node - amCharts 5 Treemap requires hierarchical data
    const hierarchicalData = [
      {
        name: initialViewText || "All",
        children: formattedData,
      },
    ]

    chart.data.setAll(hierarchicalData)

    // Configure the main series
    // In amCharts 5 Treemap, the chart itself is the series
    const series = chart

    if (series) {
      // Navigation bar (requires series to be available)
      // Check if BreadcrumbBar already exists to prevent duplicates on re-render
      const existingBreadcrumb = root.container.children.values.find(
        child => child instanceof am5hierarchy.BreadcrumbBar
      )
      if (enableNavigationBar && !existingBreadcrumb) {
        root.container.children.unshift(
          am5hierarchy.BreadcrumbBar.new(root, {
            series: series,
          })
        )
      } else if (!enableNavigationBar && existingBreadcrumb) {
        // Remove breadcrumb if it exists but navigation is now disabled
        existingBreadcrumb.dispose()
      }

      // Corner radius
      series.rectangles.template.setAll({
        cornerRadiusTL: columnCornerRadiusTopLeft,
        cornerRadiusTR: columnCornerRadiusTopRight,
        cornerRadiusBL: columnCornerRadiusBottomLeft,
        cornerRadiusBR: columnCornerRadiusBottomRight,
        strokeWidth: 1,
        stroke: am5.color(0xffffff),
      })

      // Couleur par valeur : un rectangle de treemap EST une valeur. L'adapter ne rend une
      // couleur que sur correspondance, donc `configureColorSet` reste seul a decider sans regle.
      if (colorMapper.length) {
        series.rectangles.template.adapters.add(
          "fill",
          (fill: am5.Color | undefined, target: am5.Graphics) => {
            const dataItem = target.dataItem as
              | am5.DataItem<am5hierarchy.ITreemapDataItem>
              | undefined
            const mapped = resolveMappedColor(dataItem?.get("category"), colorMapper)
            return mapped ? am5.color(mapped) : fill
          }
        )
      }

      // Tooltip
      const endTooltipText = showAsPercentage ? " %" : ""
      series.rectangles.template.set("tooltipText", `{category}: {value}${endTooltipText}`)

      // Labels
      series.labels.template.setAll({
        text: "{category}",
        centerX: am5.p50,
        centerY: am5.p50,
        textAlign: "center",
        oversizedBehavior: "hide",
      })

      // Label adapter for hiding small values and formatting
      series.labels.template.adapters.add("text", (text: string | undefined, target: any) => {
        const dataItem = target.dataItem
        if (!dataItem) return text

        const value = dataItem.get("value")

        if (
          (hideLabelIfPercentTooSmall && value < valuePercentUnderWhichHideLabel) ||
          value === 0
        ) {
          return ""
        }

        // In amCharts 5 hierarchy, 'category' contains the categoryField value
        const name = dataItem.get("category")

        if (bulletLabelType === "name_and_value") {
          return `[${bulletLabelColor} fontSize: ${bulletLabelFontSize} fontWeight: ${bulletLabelWeight}]${name}[/]\n[${bulletValueColor} fontSize: ${bulletValueFontSize} fontWeight: ${bulletValueWeight}]${value}[/]`
        } else if (bulletLabelType === "value") {
          return `[${bulletValueColor} fontSize: ${bulletValueFontSize} fontWeight: ${bulletValueWeight}]${value}[/]`
        } else {
          return `[${bulletLabelColor} fontSize: ${bulletLabelFontSize} fontWeight: ${bulletLabelWeight}]${name}[/]`
        }
      })
    }

    // Set legend data from the children of the root/home node
    const legend = root.container.children.values.find(child => child instanceof am5.Legend) as
      | am5.Legend
      | undefined

    if (legend && chart.dataItems.length > 0) {
      // Get the root data item (which contains "All")
      const rootDataItem = chart.dataItems[0]
      // Populating legend with children of the root item (level 1 categories)
      if (rootDataItem && rootDataItem.get("children")) {
        legend.data.setAll(rootDataItem.get("children"))
      }
    }
  }

  useEffect(() => {
    initChart()

    return () => {
      if (rootRef.current) {
        rootRef.current.dispose()
      }
    }
  }, [])

  useEffect(() => {
    if (chartRef.current && rootRef.current) {
      const chart = chartRef.current
      const root = rootRef.current

      // Clear legends
      clearLegends(root.container)

      // Reapply chart configurations
      setupChart(chart, root)

      // Update data
      updateChart(chart, root)
    }
  })

  return <div id={ID} style={{ width, height }} />
}

export default TreeMapChart
